import { Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useAuth from '../hooks/useAuth'
import PrivacyConsent from './PrivacyConsent'

export default function ProtectedRoute({ children, allowedRoles, requireActive = false }) {
  const { t } = useTranslation('common')
  const { user, role, status, profile, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-500 text-lg">{t('status.loading')}</div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />
  }

  if (allowedRoles && !allowedRoles.includes(role)) {
    // Redirect to the appropriate dashboard for the user's actual role
    const roleHome = role === 'admin' ? '/admin' : role === 'facilitator' ? '/facilitator' : role === 'client' ? '/client' : '/login'
    return <Navigate to={roleHome} replace />
  }

  if (requireActive && status !== 'active') {
    if (status === 'pending_approval') {
      return (
        <div className="min-h-screen flex items-center justify-center px-5">
          <div className="w-full max-w-md text-center animate-fadeIn">
            <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl">
              &#9203;
            </div>
            <h1 className="font-display text-2xl font-medium text-gray-800 mb-3">
              {t('protectedRoute.pendingApproval.title')}
            </h1>
            <p className="text-gray-600 mb-6">
              {t('protectedRoute.pendingApproval.message')}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 text-sm font-semibold text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
            >
              {t('buttons.checkAgain')}
            </button>
          </div>
        </div>
      )
    }

    if (status === 'suspended') {
      return (
        <div className="min-h-screen flex items-center justify-center px-5">
          <div className="w-full max-w-md text-center animate-fadeIn">
            <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl">
              &#10007;
            </div>
            <h1 className="font-display text-2xl font-medium text-gray-800 mb-3">
              {t('protectedRoute.suspended.title')}
            </h1>
            <p className="text-gray-600">
              {t('protectedRoute.suspended.message')}
            </p>
          </div>
        </div>
      )
    }

    if (status === 'rejected') {
      return (
        <div className="min-h-screen flex items-center justify-center px-5">
          <div className="w-full max-w-md text-center animate-fadeIn">
            <div className="w-16 h-16 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center mx-auto mb-6 text-2xl">
              &#10007;
            </div>
            <h1 className="font-display text-2xl font-medium text-gray-800 mb-3">
              {t('protectedRoute.rejected.title')}
            </h1>
            <p className="text-gray-600">
              {t('protectedRoute.rejected.message')}
            </p>
          </div>
        </div>
      )
    }
  }

  // Privacy consent gate: require for facilitators and clients (skip for admins)
  if (role !== 'admin' && profile && !profile.privacy_accepted_at) {
    return <PrivacyConsent />
  }

  return children
}
